import React from "react";
import {Button, Col, Dropdown, Form, InputGroup, Table} from "react-bootstrap";
import {Octokit} from "@octokit/core";
import {appendPullRequest, removePullRequest, swapPullRequests} from "../redux/actions";
import {connect} from "react-redux";

const PAGE_SIZE = 100;

const RepoToggle = React.forwardRef(({ children, onClick, disabled }, ref) => (
    <button
        ref={ref}
        type="button"
        disabled={disabled}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
        className="form-control text-left d-flex justify-content-between align-items-center dropdown-toggle"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', backgroundColor: '#fff' }}
    >
        {children}
    </button>
));
RepoToggle.displayName = 'RepoToggle';

class PullRequestsSelectorComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedRepository: '',
            selectedPullRequest: '',
            loadingPullRequests: false,
            availablePullRequests: [],
            repositoryFilter: '',
            repoPage: 1
        };
    }

    repositoryOnSelect(repoIdOrEvent) {
        const value = repoIdOrEvent && repoIdOrEvent.target ? repoIdOrEvent.target.value : repoIdOrEvent;
        if (!this.state.loadingPullRequests && value) {
            this.setState((state) => ({
                ...state,
                selectedRepository: value,
                availablePullRequests: [],
                loadingPullRequests: true,
                selectedPullRequest: ''
            }));
            const { githubToken, githubRepositories } = this.props;
            const octokit = new Octokit({ auth: githubToken.accessToken });
            const repo = githubRepositories.find(({ id }) => id.toString() === value);
            if (repo) {
                octokit.request(`GET ${repo.url}/pulls`)
                    .then(({ data }) => ({ data: data.filter(({ base: { ref } }) => ref === 'main' || ref === 'master') }))
                    .then(({ data }) => {
                        this.setState((state) => ({
                            ...state,
                            availablePullRequests: data,
                            loadingPullRequests: false,
                            selectedPullRequest: ''
                        }));
                    }).catch((error) => {
                        console.error(error);
                    });
            }
        }
    }

    addPullRequest() {
        const { availablePullRequests, selectedPullRequest } = this.state;
        const pullRequest = availablePullRequests.find(({ id }) => id.toString() === selectedPullRequest);
        this.props.appendPullRequest(pullRequest);
        this.setState((state) => ({
            ...state,
            selectedPullRequest: '',
            selectedRepository: '',
            availablePullRequests: [],
            repositoryFilter: '',
            repoPage: 1
        }));
    }

    render() {
        const { pullRequests, fetchingGithubRepositories, githubRepositories } = this.props;
        const { selectedRepository, selectedPullRequest, loadingPullRequests, availablePullRequests, repositoryFilter, repoPage } = this.state;

        const filteredRepositories = githubRepositories.filter((repo) => {
            if (repo.archived) return false;
            return !repositoryFilter || repo.name.toLowerCase().includes(repositoryFilter.toLowerCase());
        });

        const totalPages = Math.max(1, Math.ceil(filteredRepositories.length / PAGE_SIZE));
        const safePage = Math.min(Math.max(1, repoPage), totalPages);
        const startIndex = (safePage - 1) * PAGE_SIZE;
        const currentReposPage = filteredRepositories.slice(startIndex, startIndex + PAGE_SIZE);

        const selectedRepo = githubRepositories.find(({ id }) => id.toString() === selectedRepository);

        return <div>
            <Table size="sm">
                <tbody>
                    { pullRequests.map((pullRequest, i) => <tr key={pullRequest.id}>
                        <td>{i + 1}.</td>
                        <td>{ pullRequest.base.repo.name }</td>
                        <td>{ pullRequest.title }</td>
                        <td>
                            <Button
                                variant="link"
                                size="sm"
                                className="text-danger"
                                onClick={() => this.props.removePullRequest(pullRequest.id)}
                            >
                                <i className="fas fa-times" />
                            </Button>
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => this.props.swapPullRequests(i, i - 1)}
                                disabled={i === 0}
                            >
                                <i className="fas fa-arrow-up" />
                            </Button>
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => this.props.swapPullRequests(i, i + 1)}
                                disabled={i + 1 === pullRequests.length}
                            >
                                <i className="fas fa-arrow-down" />
                            </Button>
                        </td>
                    </tr>) }
                    <tr><td colSpan={4} /></tr>
                </tbody>
            </Table>
            <Form.Row>
                <Col sm>
                    <Dropdown className="w-100">
                        <Dropdown.Toggle
                            as={RepoToggle}
                            id="dropdown-repositories"
                            disabled={fetchingGithubRepositories}
                        >
                            <span className={selectedRepo ? "text-dark text-truncate" : "text-muted text-truncate"}>
                                { fetchingGithubRepositories
                                    ? "Loading repos..."
                                    : (selectedRepo ? selectedRepo.name : "Select a repo...") }
                            </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="w-100 shadow p-0 bg-white" style={{ minWidth: '280px', zIndex: 1050 }}>
                            <div className="p-2 border-bottom bg-light" onClick={(e) => e.stopPropagation()}>
                                <InputGroup size="sm">
                                    <InputGroup.Prepend>
                                        <InputGroup.Text>
                                            <i className="fas fa-search" />
                                        </InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search repository..."
                                        value={repositoryFilter}
                                        autoFocus
                                        onChange={({ target: { value } }) => this.setState({ repositoryFilter: value, repoPage: 1 })}
                                        onKeyDown={(e) => e.stopPropagation()}
                                    />
                                    { repositoryFilter && (
                                        <InputGroup.Append>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => this.setState({ repositoryFilter: '', repoPage: 1 })}
                                            >
                                                <i className="fas fa-times" />
                                            </Button>
                                        </InputGroup.Append>
                                    ) }
                                </InputGroup>
                            </div>

                            <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                { currentReposPage.length === 0 ? (
                                    <div className="text-muted text-center py-3 small">
                                        { fetchingGithubRepositories ? "Loading repos..." : "No repositories found" }
                                    </div>
                                ) : (
                                    currentReposPage.map((repo) => (
                                        <Dropdown.Item
                                            key={repo.id}
                                            active={repo.id.toString() === selectedRepository}
                                            onClick={() => this.repositoryOnSelect(repo.id.toString())}
                                        >
                                            { repo.name }
                                        </Dropdown.Item>
                                    ))
                                ) }
                            </div>

                            { totalPages > 1 && (
                                <div
                                    className="p-2 border-top bg-light d-flex justify-content-between align-items-center small"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={safePage <= 1}
                                        onClick={() => this.setState((state) => ({ repoPage: Math.max(1, state.repoPage - 1) }))}
                                    >
                                        <i className="fas fa-chevron-left mr-1" /> Prev
                                    </Button>
                                    <span className="text-muted font-weight-bold">
                                        Page { safePage } of { totalPages } ({ filteredRepositories.length } total)
                                    </span>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        disabled={safePage >= totalPages}
                                        onClick={() => this.setState((state) => ({ repoPage: Math.min(totalPages, state.repoPage + 1) }))}
                                    >
                                        Next <i className="fas fa-chevron-right ml-1" />
                                    </Button>
                                </div>
                            ) }
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
                <Col sm>
                    <Form.Control
                        as="select"
                        value={selectedPullRequest}
                        disabled={selectedRepository === '' || loadingPullRequests}
                        onChange={({ target: { value } }) => {
                            this.setState((state) => ({ ...state, selectedPullRequest: value }));
                        }}
                    >
                        <option value="" disabled>
                            { loadingPullRequests ? "Loading PRs..." : "Select a pull request..." }
                        </option>
                        { availablePullRequests.map(({ id, title }) =>
                            <option key={id} value={id}>{ title }</option>
                        ) }
                    </Form.Control>
                </Col>
                <Col sm={2}>
                    <Button
                        variant="primary"
                        disabled={selectedPullRequest === ''}
                        onClick={this.addPullRequest.bind(this)}
                    >
                        Add
                    </Button>
                </Col>
            </Form.Row>
        </div>;
    }
}

export default connect(
    ({ auth: { githubToken }, releaseTicket }) => ({ ...releaseTicket, githubToken }),
    { appendPullRequest, removePullRequest, swapPullRequests }
)(PullRequestsSelectorComponent);
